import { cn } from "@/lib/utils";
import type {
	NavigationAufgabenstellungDTO,
	NavigationKlausurDTO,
	NavigationSachverhaltDTO,
} from "@/types/klausurEditor.types";
import { ArrowLeft, PanelRightClose, PanelRightOpen } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";

/**
 * Transforms different data formats into NavigationKlausurDTO or NavigationAufgabenstellungDTO[]
 * for use with the KlausurTableOfContents component.
 *
 * @param klausurData The klausurData to transform (UserKlausurDTO or evaluation klausurData)
 * @returns Navigation format suitable for the table of contents
 */
export function transformToNavigation(
	klausurData: NavigationKlausurDTO,
): NavigationKlausurDTO {
	return {
		id: klausurData.id,
		title: klausurData.title,
		aufgabenstellungen: klausurData.aufgabenstellungen.map(
			(aufgabenstellung) => ({
				id: aufgabenstellung.id,
				title: aufgabenstellung.title || "Ohne Benennung",
				index: aufgabenstellung.index || 0,
				sachverhalte: aufgabenstellung.sachverhalte.map((sachverhalt) => ({
					id: sachverhalt.id,
					title: sachverhalt.title || "Ohne Benennung",
					index: sachverhalt.index || 0,
				})),
			}),
		),
	} as NavigationKlausurDTO;
}

/**
 * KlausurTableOfContents is a component that displays the table of contents for a klausur.
 * It is used in the klausurWriting feature.
 *
 * @note use the transformToNavigation function to transform the klausur data to the NavigationKlausurDTO format.
 *
 * The component receives the klausur data and callback functions for when a sachverhalt
 * or aufgabenstellung is clicked. It also tracks the currently active sachverhalt and
 * aufgabenstellung through props, allowing the parent component to control the highlighted
 * items in the table of contents.
 * Per default active Aufgabenstellung is the first Aufgabenstellung in the klausur.
 *
 * @param props.klausur - The klausur data containing sachverhalte and aufgabenstellungen
 * @param props.onSachverhaltClick - Callback when a sachverhalt is clicked
 * @param props.onAufgabenstellungClick - Optional callback when an aufgabenstellung is clicked
 * @param props.activeSachverhalt - Currently active/selected sachverhalt to highlight in the TOC
 * @param props.activeAufgabenstellung - Currently active/selected aufgabenstellung to highlight in the TOC
 * @param props.backButton - Optional boolean to show a back button which goes to prev page
 */

export function KlausurTableOfContents(props: {
	klausur: NavigationKlausurDTO;
	onSachverhaltClick?: (sachverhalt: NavigationSachverhaltDTO) => void;
	onAufgabenstellungClick?: (
		aufgabenstellung: NavigationAufgabenstellungDTO,
	) => void;
	activeSachverhalt?: NavigationSachverhaltDTO | null;
	activeAufgabenstellung?: NavigationAufgabenstellungDTO | null;
	backButton?: boolean;
	isCollapsed?: boolean;
}) {
	const [isMinimized, setIsMinimized] = useState(props.isCollapsed ?? false);
	const navigate = useNavigate();

	/**
	 * This function is called when a sachverhalt is clicked.
	 * It will call the onSachverhaltClick function of the parent component.
	 * @param sachverhalt The sachverhalt that was clicked.
	 */
	const handleSachverhaltClick = (sachverhalt: NavigationSachverhaltDTO) => {
		// First call the parent's callback to update state
		if (props.onSachverhaltClick) {
			props.onSachverhaltClick(sachverhalt);
		}

		// Then use setTimeout to delay the scroll event to happen after state update, push it to the end of the event loop
		setTimeout(() => {
			const scrollEvent = new CustomEvent("scrollToItem", {
				detail: {
					itemId: sachverhalt.id,
					itemType: "sachverhalt",
				},
			});
			document.dispatchEvent(scrollEvent);
		}, 0);
	};

	/**
	 * This function is called when an aufgabenstellung is clicked.
	 * It will call the onAufgabenstellungClick function of the parent component if provided.
	 * @param aufgabenstellung The aufgabenstellung that was clicked.
	 */
	const handleAufgabenstellungClick = (
		aufgabenstellung: NavigationAufgabenstellungDTO,
	) => {
		// First call the parent's callback to update the state
		if (props.onAufgabenstellungClick) {
			props.onAufgabenstellungClick(aufgabenstellung);
		}

		// Use setTimeout to allow React state to update before scrolling
		setTimeout(() => {
			// Dispatch custom event to scroll to the aufgabenstellung in the editor
			const scrollEvent = new CustomEvent("scrollToItem", {
				detail: {
					itemId: aufgabenstellung.id,
					itemType: "aufgabenstellung",
				},
			});
			document.dispatchEvent(scrollEvent);
		}, 0);
	};

	useEffect(() => {
		const handleScrollToItem = (event: CustomEvent) => {
			const { itemId } = event.detail;
			const element = document.querySelector(`[data-item-id="${itemId}"]`);

			if (element) {
				// Find the scrollable parent container
				let scrollableParent = element.parentElement;
				while (scrollableParent) {
					const overflow = window.getComputedStyle(scrollableParent).overflowY;
					if (overflow === "auto" || overflow === "scroll") {
						break;
					}
					scrollableParent = scrollableParent.parentElement;
				}

				if (scrollableParent) {
					// Calculate position relative to scrollable container
					const elementRect = element.getBoundingClientRect();
					const parentRect = scrollableParent.getBoundingClientRect();
					const scrollTop = scrollableParent.scrollTop;
					const targetScroll =
						scrollTop + (elementRect.top - parentRect.top) - 10; // 10px offset from top

					// Smooth scroll within the container
					scrollableParent.scrollTo({
						top: targetScroll,
						behavior: "smooth",
					});
				} else {
					// Fallback to scrollIntoView
					element.scrollIntoView({ behavior: "smooth", block: "start" });
				}
			}
		};

		document.addEventListener(
			"scrollToItem",
			handleScrollToItem as EventListener,
		);

		return () => {
			document.removeEventListener(
				"scrollToItem",
				handleScrollToItem as EventListener,
			);
		};
	}, []);

	return (
		<div
			className={cn(
				"relative h-full flex flex-col bg-gradient-to-br from-muted/20 to-muted/5 backdrop-blur-sm transition-all duration-200",
				isMinimized ? "w-12" : "w-72",
			)}
		>
			<div className="h-full flex flex-col overflow-hidden">
				{/* Back Button Area */}
				{props.backButton && (
					<div className="border-b bg-background/50 backdrop-blur-sm p-2">
						<Button
							variant="outline"
							size={isMinimized ? "icon" : "sm"}
							onClick={() => navigate(-1)}
							className={cn(
								"flex items-center transition-all duration-150",
								isMinimized ? "w-8 h-8 mx-auto p-0" : "w-full gap-2",
							)}
							aria-label="Zurück zur Klausurübersicht"
						>
							<ArrowLeft className="h-4 w-4 flex-shrink-0" />
							{!isMinimized && (
								<span className="whitespace-nowrap">Zurück zur Übersicht</span>
							)}
						</Button>
					</div>
				)}

				{/* Header */}
				<div
					className={cn(
						"flex items-center border-b bg-background/50 backdrop-blur-sm transition-all duration-200 select-none",
						isMinimized
							? "justify-center px-2 py-2"
							: "justify-between px-4 py-3",
					)}
				>
					<h2
						className={cn(
							"text-base font-semibold transition-opacity duration-150 pointer-events-none",
							isMinimized ? "opacity-0 w-0 absolute" : "opacity-100",
						)}
					>
						Inhaltsverzeichnis
					</h2>
					<Button
						variant="ghost"
						size="icon"
						onClick={() => setIsMinimized(!isMinimized)}
						aria-label={
							isMinimized
								? "Expand table of contents"
								: "Minimize table of connections"
						}
						className="h-8 w-8 hover:bg-muted transition-colors flex-shrink-0"
					>
						{isMinimized ? (
							<PanelRightClose size={18} />
						) : (
							<PanelRightOpen size={18} />
						)}
					</Button>
				</div>

				{/* Scrollable content */}
				<div
					className={cn(
						"flex-grow transition-all duration-200",
						isMinimized
							? "opacity-0 h-0 overflow-hidden"
							: "opacity-100 overflow-y-auto px-3 py-4",
					)}
				>
					{!props.klausur.aufgabenstellungen ||
					props.klausur.aufgabenstellungen.length === 0 ? (
						<div className="text-sm text-muted-foreground italic text-center py-8">
							Keine Aufgabenstellungen vorhanden
						</div>
					) : (
						<div className="space-y-3">
							{props.klausur.aufgabenstellungen.map(
								(
									aufgabenstellung: NavigationAufgabenstellungDTO,
									index: number,
								) => (
									<div
										key={aufgabenstellung.id}
										className="rounded-lg bg-background/50 backdrop-blur-sm transition-all duration-200"
									>
										{/* Aufgabenstellung Header */}
										<button
											type="button"
											className="w-full text-left p-3 flex items-start gap-2 group"
											onClick={() =>
												handleAufgabenstellungClick(
													aufgabenstellung as unknown as NavigationAufgabenstellungDTO,
												)
											}
										>
											<span
												className={cn(
													"flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-md text-xs font-semibold transition-colors",
													props.activeAufgabenstellung?.id ===
														aufgabenstellung.id
														? "bg-primary text-primary-foreground"
														: "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary",
												)}
											>
												{index + 1}
											</span>
											<span
												className={cn(
													"flex-1 font-medium text-sm leading-relaxed transition-colors",
													props.activeAufgabenstellung?.id ===
														aufgabenstellung.id
														? "text-primary"
														: "text-foreground group-hover:text-primary",
												)}
											>
												{aufgabenstellung.title || "Ohne Benennung"}
											</span>
										</button>

										{/* Sachverhalte List */}
										{aufgabenstellung.sachverhalte &&
											aufgabenstellung.sachverhalte.length > 0 && (
												<div className="pt-1">
													<div className="px-3 pb-2 pt-1.5 space-y-0.5">
														{aufgabenstellung.sachverhalte.map(
															(
																sachverhalt: NavigationSachverhaltDTO,
																svIndex: number,
															) => (
																<button
																	type="button"
																	key={sachverhalt.id}
																	className={cn(
																		"w-full text-left px-2 py-1.5 rounded-md flex items-start gap-2 group transition-all duration-150",
																		props.activeSachverhalt?.id ===
																			sachverhalt.id
																			? "bg-primary/10 text-primary"
																			: "text-muted-foreground hover:bg-muted hover:text-foreground",
																	)}
																	onClick={() =>
																		handleSachverhaltClick(sachverhalt)
																	}
																>
																	<span
																		className={cn(
																			"flex-shrink-0 text-xs font-medium min-w-[2rem] transition-colors",
																			props.activeSachverhalt?.id ===
																				sachverhalt.id
																				? "text-primary"
																				: "text-muted-foreground/70 group-hover:text-foreground",
																		)}
																	>
																		{index + 1}.{svIndex + 1}
																	</span>
																	<span className="flex-1 text-sm leading-relaxed">
																		{sachverhalt.title || "Ohne Benennung"}
																	</span>
																</button>
															),
														)}
													</div>
												</div>
											)}

										{/* Empty state for sachverhalte */}
										{(!aufgabenstellung.sachverhalte ||
											aufgabenstellung.sachverhalte.length === 0) && (
											<div className="px-3 pb-3 pt-1 text-xs text-muted-foreground italic">
												Keine Sachverhalte vorhanden
											</div>
										)}
									</div>
								),
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
